import React, { useState, useEffect } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor, Essentials, Paragraph, Heading,
  Bold, Italic, Underline, Strikethrough,
  Link, BlockQuote, Alignment,
  List, Indent,
  Image, ImageCaption, ImageStyle, ImageToolbar, ImageUpload, ImageResize,
  Table, TableToolbar, TableCellProperties, TableProperties,
  MediaEmbed, HorizontalLine,
  FontColor, FontSize, Undo,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { getFirestore, doc, getDoc, updateDoc, deleteDoc, addDoc, collection, query, where, orderBy, getDocs, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import app from '../firebase';
import './BlogManager.css';

// ─── SETUP (run once) ─────────────────────────────────────────────────────────
// npm install ckeditor5 @ckeditor/ckeditor5-react
// Remove the CKEditor <script> tag from index.html entirely.
// ─────────────────────────────────────────────────────────────────────────────

const db = getFirestore(app);
const storage = getStorage(app);

// Firebase image upload adapter — uploads inline images to Firebase Storage
function FirebaseUploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => ({
    upload() {
      return loader.file.then(file => {
        const storageRef = ref(storage, `images/content/${Date.now()}_${file.name}`);
        return uploadBytes(storageRef, file)
          .then(snap => getDownloadURL(snap.ref))
          .then(url => ({ default: url }));
      });
    },
    abort() {},
  });
}

// All plugins for the editor
const EDITOR_PLUGINS = [
  Essentials, Paragraph, Heading, Undo,
  Bold, Italic, Underline, Strikethrough,
  Link, BlockQuote, Alignment,
  List, Indent,
  Image, ImageCaption, ImageStyle, ImageToolbar, ImageUpload, ImageResize,
  Table, TableToolbar, TableCellProperties, TableProperties,
  MediaEmbed, HorizontalLine,
  FontColor, FontSize,
];

const EDITOR_TOOLBAR = [
  'heading', '|',
  'bold', 'italic', 'underline', 'strikethrough', '|',
  'fontSize', 'fontColor', '|',
  'alignment', '|',
  'link', 'blockQuote', 'horizontalLine', '|',
  'bulletedList', 'numberedList', 'outdent', 'indent', '|',
  'imageUpload', 'insertTable', 'mediaEmbed', '|',
  'undo', 'redo',
];

const EDITOR_CONFIG = {
  licenseKey: 'GPL',
  plugins: EDITOR_PLUGINS,
  extraPlugins: [FirebaseUploadAdapterPlugin],
  toolbar: { items: EDITOR_TOOLBAR, shouldNotGroupWhenFull: true },
  image: {
    toolbar: ['imageTextAlternative', '|', 'imageStyle:inline', 'imageStyle:block', 'imageStyle:side', '|', 'imageResize'],
  },
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'],
  },
  mediaEmbed: {
    previewsInData: true,
    // YouTube, Vimeo, etc. are supported by default
  },
};

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [currentEditingId, setCurrentEditingId] = useState(null);
  const [editorContent, setEditorContent] = useState(''); // replaces editorInstance
  const [comments, setComments] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    type: 'Strategy',
    status: 'published',
    short_description: '',
    content: '',
    key_takeaways: '',
    author_name: 'RockPaperRunway',
    author_title: '',
    author_bio: '',
    author_linkedin: ''
  });

  const [featuredImage, setFeaturedImage] = useState(null);
  const [authorAvatar, setAuthorAvatar] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'posts'), orderBy('published_at', 'desc'));
      const snapshot = await getDocs(q);
      const postsData = [];
      snapshot.forEach(doc => {
        postsData.push({ id: doc.id, ...doc.data() });
      });
      setPosts(postsData);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (text) => {
    return text.toString().toLowerCase().trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate slug from title
    if (name === 'title') {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }
  };

  const uploadFile = async (file, path) => {
    if (!file) return null;
    const storageRef = ref(storage, `images/${path}/${Date.now()}_${file.name}`);
    const snapshot = await uploadBytes(storageRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.slug) {
      alert('Slug is required');
      return;
    }

    try {
      // Check slug uniqueness
      const q = query(collection(db, 'posts'), where('slug', '==', formData.slug));
      const snapshot = await getDocs(q);
      const isUnique = !snapshot.size || (currentEditingId && snapshot.docs[0].id === currentEditingId);
      
      if (!isUnique) {
        alert('This slug already exists. Please choose a different one.');
        return;
      }

      // Get existing data if editing
      let existingData = {};
      if (currentEditingId) {
        const snap = await getDoc(doc(db, 'posts', currentEditingId));
        if (snap.exists()) existingData = snap.data();
      }

      // Upload files
      const featuredImageUrl = await uploadFile(featuredImage, 'featured') || existingData.featured_image_url || '';
      const authorAvatarUrl = await uploadFile(authorAvatar, 'avatars') || (existingData.author && existingData.author.avatar_url) || '';

      // Get content from CKEditor React component state
      const contentHtml = editorContent || formData.content;

      const postData = {
        title: formData.title,
        slug: formData.slug,
        type: formData.type,
        status: formData.status,
        short_description: formData.short_description,
        featured_image_url: featuredImageUrl,
        content: contentHtml,
        key_takeaways: formData.key_takeaways.split('\n').filter(Boolean),
        published_at: existingData.published_at || serverTimestamp(),
        updated_at: serverTimestamp(),
        author: {
          name: formData.author_name,
          title: formData.author_title,
          bio: formData.author_bio,
          avatar_url: authorAvatarUrl,
          linkedin_url: formData.author_linkedin
        }
      };

      if (currentEditingId) {
        await updateDoc(doc(db, 'posts', currentEditingId), postData);
        alert('✅ Post Updated!');
      } else {
        await addDoc(collection(db, 'posts'), postData);
        alert('✅ Post Created!');
      }

      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Error: ' + error.message);
    }
  };

  const handleEdit = async (id) => {
    try {
      const snap = await getDoc(doc(db, 'posts', id));
      if (snap.exists()) {
        const data = snap.data();
        setFormData({
          title: data.title,
          slug: data.slug,
          type: data.type,
          status: data.status,
          short_description: data.short_description,
          content: data.content || '',
          key_takeaways: (data.key_takeaways || []).join('\n'),
          author_name: data.author?.name || 'RockPaperRunway',
          author_title: data.author?.title || '',
          author_bio: data.author?.bio || '',
          author_linkedin: data.author?.linkedin_url || ''
        });
        setCurrentEditingId(id);
        setEditorContent(data.content || '');
        setEditing(true);

        // Fetch comments
        fetchComments(id);
      }
    } catch (error) {
      console.error('Error loading post:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deleteDoc(doc(db, 'posts', id));
        alert('Post deleted successfully');
        fetchPosts();
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Error deleting post');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      type: 'Strategy',
      status: 'published',
      short_description: '',
      content: '',
      key_takeaways: '',
      author_name: 'RockPaperRunway',
      author_title: '',
      author_bio: '',
      author_linkedin: ''
    });
    setFeaturedImage(null);
    setAuthorAvatar(null);
    setCurrentEditingId(null);
    setEditing(false);
    setComments([]);
    setEditorContent('');
  };

  const fetchComments = async (postId) => {
    try {
      const q = query(collection(db, 'posts', postId, 'comments'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const commentsData = [];
      snap.forEach(d => {
        commentsData.push({ id: d.id, ...d.data() });
      });
      setComments(commentsData);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const deleteComment = async (postId, commentId) => {
    if (window.confirm('Delete this comment?')) {
      try {
        await deleteDoc(doc(db, 'posts', postId, 'comments', commentId));
        fetchComments(postId);
      } catch (error) {
        console.error('Error deleting comment:', error);
      }
    }
  };

  if (editing) {
    return (
      <div className="blog-form-view">
        <div className="blog-form-container">
          <h2>{currentEditingId ? 'Edit Blog Post' : 'Add New Blog Post'}</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Title:</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="slug">URL Slug:</label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                title="Lowercase, numbers, hyphens only"
                required
              />
              <div className="slug-preview">
                URL: <strong>/blog/{formData.slug}</strong>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="type">Category:</label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                >
                  <option value="Strategy">Strategy</option>
                  <option value="Branding">Branding</option>
                  <option value="Design">Design</option>
                  <option value="Performance">Performance</option>
                  <option value="Technology">Technology</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="status">Status:</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="featured_image">Featured Image:</label>
              <input
                type="file"
                id="featured_image"
                accept="image/*"
                onChange={(e) => setFeaturedImage(e.target.files[0])}
              />
            </div>

            <div className="form-group">
              <label htmlFor="short_description">Short Description:</label>
              <textarea
                id="short_description"
                name="short_description"
                rows="3"
                value={formData.short_description}
                onChange={handleInputChange}
                required
              />
            </div>

            <h3 className="section-divider">Content</h3>
            <div className="form-group">
              <CKEditor
                editor={ClassicEditor}
                config={EDITOR_CONFIG}
                data={editorContent}
                onChange={(event, editor) => setEditorContent(editor.getData())}
              />
            </div>

            <h3 className="section-divider">Meta</h3>
            <div className="form-group">
              <label htmlFor="key_takeaways">Key Takeaways (one per line):</label>
              <textarea
                id="key_takeaways"
                name="key_takeaways"
                rows="4"
                value={formData.key_takeaways}
                onChange={handleInputChange}
              />
            </div>

            <h3 className="section-divider">Author</h3>
            <div className="form-group">
              <label htmlFor="author_name">Author Name:</label>
              <input
                type="text"
                id="author_name"
                name="author_name"
                value={formData.author_name}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="author_title">Author Title:</label>
              <input
                type="text"
                id="author_title"
                name="author_title"
                value={formData.author_title}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="author_bio">Author Bio:</label>
              <textarea
                id="author_bio"
                name="author_bio"
                rows="2"
                value={formData.author_bio}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="author_avatar">Avatar:</label>
              <input
                type="file"
                id="author_avatar"
                accept="image/*"
                onChange={(e) => setAuthorAvatar(e.target.files[0])}
              />
            </div>

            <div className="form-group">
              <label htmlFor="author_linkedin">LinkedIn URL:</label>
              <input
                type="text"
                id="author_linkedin"
                name="author_linkedin"
                value={formData.author_linkedin}
                onChange={handleInputChange}
              />
            </div>

            {currentEditingId && comments.length > 0 && (
              <>
                <h3 className="section-divider">Comments</h3>
                <div className="comments-list">
                  {comments.map(comment => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-details">
                        <span className="comment-author">{comment.name}</span>
                        <span className="comment-text">{comment.commentText}</span>
                        <span className="comment-date">
                          {comment.createdAt ? comment.createdAt.toDate().toLocaleDateString() : ''}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => deleteComment(currentEditingId, comment.id)}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="form-actions">
              <button type="submit" className="btn">
                {currentEditingId ? 'Update Post' : 'Save Post'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-manager">
      <div className="blog-header">
        <h2>All Posts</h2>
        <button className="btn" onClick={() => setEditing(true)}>
          Add New Post
        </button>
      </div>

      {loading ? (
        <p>Loading posts...</p>
      ) : (
        <div className="blog-grid">
          {posts.length === 0 ? (
            <p>No posts found.</p>
          ) : (
            posts.map(post => (
              <div key={post.id} className="post-card">
                <div className="post-card-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleEdit(post.id)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(post.id)}
                  >
                    Delete
                  </button>
                </div>
                
                <img
                  src={post.featured_image_url || 'https://via.placeholder.com/300x180?text=No+Image'}
                  alt={post.title}
                  className="post-card-image"
                />
                
                <div className="post-card-content">
                  <span className={`post-card-status status-${post.status}`}>
                    {post.status}
                  </span>
                  <h3 className="post-card-title">{post.title}</h3>
                  <div className="post-card-meta">
                    {post.type} • {post.author?.name || 'RPR'}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}